import { useTranslation } from "react-i18next";
import Member from "./Member";
import { Fragment } from "react/jsx-runtime";
import { useNetworkStore } from "@/modules/auth/store/network";
import { useEffect, useMemo, useRef } from "react";
import { Icon } from "@/components/common/icon/Icon";

const LeadersTeam = () => {
  const { t } = useTranslation();

  const { tree, path, filter, filtered, hasMoreMap, setToTree, isLoading } =
    useNetworkStore();

  const loaderRef = useRef<HTMLDivElement | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);

  const currentChildren = useMemo(() => {
    if (!path) return tree;

    const pathArr = path
      .split(",")
      .map((i) => i.trim().toUpperCase())
      .filter((i) => i !== "");

    let currentLevel = tree;

    for (let i = 0; i < pathArr.length; i++) {
      const refCode = pathArr[i];

      const node = currentLevel.find(
        (r) => r.refCode.toUpperCase() === refCode
      );

      if (!node) return [];

      if (!node.children) return null;

      if (i === pathArr.length - 1) {
        return node.children;
      }

      currentLevel = node.children || [];
    }

    return tree;
  }, [path, tree]);

  const displayMembers =
    filtered.length >= 0 && filter.length >= 3
      ? filtered
      : currentChildren
      ? currentChildren
      : [];

  useEffect(() => {
    if (!loaderRef.current || isLoading) return;

    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (
        entries[0].isIntersecting &&
        hasMoreMap[path] !== false &&
        !isLoading
      ) {
        setToTree(
          path
            ? { path, offset: currentChildren?.length || 0 }
            : { offset: currentChildren?.length || 0 }
        );
      }
    });

    observer.current.observe(loaderRef.current);

    return () => observer.current?.disconnect();
  }, [
    loaderRef,
    isLoading,
    hasMoreMap,
    path,
    setToTree,
    currentChildren?.length,
  ]);

  return (
    <div className="flex-1 w-full flex flex-col">
      {isLoading && displayMembers.length === 0 && (
        <div className="w-full flex-1 py-6 flex items-center justify-center">
          <Icon type="loading" className="w-10 h-10 fill-link animate-spin" />
        </div>
      )}
      {displayMembers.length > 0 && (
        <>
          <div className="flex flex-col bg-bg rounded-[0.75rem]">
            {displayMembers.map((member, index) => (
              <Fragment key={index}>
                <Member
                  key={index}
                  telegramUsername={member.telegramUsername}
                  refCode={member.refCode}
                  teamVolumeUsd={member.teamVolumeUsd}
                  name={member.name}
                  ownDeposit={member.ownDeposit}
                  referralVolumeUsd={member.referralVolumeUsd}
                />

                {index !== displayMembers.length - 1 && (
                  <div className="w-full pl-18">
                    <div className="w-full h-px bg-divider"></div>
                  </div>
                )}
              </Fragment>
            ))}
          </div>
          {(hasMoreMap[path] || hasMoreMap[path] === undefined) && (
            <div className="w-full flex items-center justify-center pt-4">
              <Icon
                type="loading"
                className="w-10 h-10 fill-link animate-spin"
              />
            </div>
          )}
          <p className="px-4 pt-4 text-[0.8125rem] leading-[1rem] text-section-header-text tracking-tighter text-center">
            {t("network-page.leaders_team")}
          </p>
        </>
      )}
      {(hasMoreMap[path] || hasMoreMap[path] === undefined) && (
        <div ref={loaderRef} className="w-full h-px"></div>
      )}
      {displayMembers.length === 0 && !isLoading && (
        <p className="text-[1.0625rem] leading-[1.375rem] text-hint text-center p-4">
          {t("network-page.no_data")}
        </p>
      )}
    </div>
  );
};

export default LeadersTeam;
